import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type Api } from "@api/base-api-request";
import {
  AddUserWalletParams,
  AddUserWalletResponse,
  BulkDeleteWallets,
  DeleteUserWalletParams,
  LowRoiWalletsResponse,
} from "./types";

const QUERY_KEY_ADD_USER_WALLET = "add-user-wallet";
const QUERY_KEY_DELETE_USER_WALLET = "delete-user-wallet";
const QUERY_KEY_BULK_DELETE_WALLETS = "bulk-delete-wallets";
const QUERY_KEY_GET_LOW_ROI_WALLETS = "get-low-roi-wallets";

const addUserWallet = async (
  api: Api,
  params: AddUserWalletParams,
): Promise<AddUserWalletResponse> => {
  return api.post("walletManagement/addUserWallet", { json: params }).json();
};

const deleteUserWallet = async (
  api: Api,
  params: DeleteUserWalletParams,
): Promise<void> => {
  return api.post("walletManagement/deleteUserWallet", { json: params }).json();
};

const bulkDeleteWallets = async (
  api: Api,
  params: BulkDeleteWallets,
): Promise<void> => {
  return api
    .post("walletManagement/bulkDeleteWallets", { json: params })
    .json();
};

const getLowRoiWalletAddresses = async (
  api: Api,
  { thresholdPercentage }: { thresholdPercentage: number },
): Promise<LowRoiWalletsResponse> => {
  return api
    .get("walletManagement/getWalletAddressesWithLowNetPnl", {
      searchParams: { thresholdPercentage },
    })
    .json();
};

const useAddUserWallet = () =>
  useMutation({
    mutationKey: [QUERY_KEY_ADD_USER_WALLET],
    mutationFn: (payload: AddUserWalletParams) => addUserWallet(api, payload),
  });

const useDeleteUserWallet = () =>
  useMutation({
    mutationKey: [QUERY_KEY_DELETE_USER_WALLET],
    mutationFn: (payload: DeleteUserWalletParams) =>
      deleteUserWallet(api, payload),
  });

const useBulkDeleteWallets = () =>
  useMutation({
    mutationKey: [QUERY_KEY_BULK_DELETE_WALLETS],
    mutationFn: (payload: BulkDeleteWallets) => bulkDeleteWallets(api, payload),
  });

const useGetLowRoiWalletAddresses = (thresholdPercentage: number) =>
  useQuery({
    queryKey: [QUERY_KEY_GET_LOW_ROI_WALLETS, thresholdPercentage],
    queryFn: () => getLowRoiWalletAddresses(api, { thresholdPercentage }),
  });

export {
  useAddUserWallet,
  useDeleteUserWallet,
  useBulkDeleteWallets,
  useGetLowRoiWalletAddresses,
  addUserWallet,
  deleteUserWallet,
  getLowRoiWalletAddresses,
  QUERY_KEY_ADD_USER_WALLET as ADD_USER_WALLET_QUERY_KEY,
  QUERY_KEY_DELETE_USER_WALLET as DELETE_USER_WALLET_QUERY_KEY,
  QUERY_KEY_BULK_DELETE_WALLETS as BULK_DELETE_WALLETS_QUERY_KEY,
  QUERY_KEY_GET_LOW_ROI_WALLETS as GET_LOW_ROI_WALLETS_QUERY_KEY,
  type AddUserWalletParams as AddUserWalletPayload,
  type AddUserWalletResponse as AddUserWalletResponse,
  type DeleteUserWalletParams as DeleteUserWalletPayload,
};
